import React, { useEffect, useState, Fragment, useRef } from "react";
import { Dropdown, Form, Modal, Row, Col } from "react-bootstrap";

import { useStudentsData } from "../../../context/StudentsContext";
import { useGrade } from "../../../context/GradeContext";

import AdmissionModal from "../Modals/AdmissionModal";
import StudentLedgerModal from "../Student/StudentLedgerModal";
import EditStudentModal from "../Modals/EditStudentModal";

const Enrollment = () => {

  const [showView, setShowView] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showPhoto, setShowPhoto] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const { studentsData, refreshData, loading } = useStudentsData();
  const { grade } = useGrade();

  const admissionRef = useRef();

  const [selectedGrade, setSelectedGrade] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showLedger, setShowLedger] = useState(false);

  // ===============================
  // PAGINATION STATE
  // ===============================
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const currentYear = new Date().getFullYear();

  const openView = (student) => {
    setSelectedStudent(student);
    setShowView(true);
  };

  const openEdit = (student) => {
    setSelectedStudent(student);
    setShowEdit(true);
  };

  const openPhotoModal = (photo) => {
    setSelectedPhoto(photo);
    setShowPhoto(true);
  };

  // ===============================
  // FILTER CURRENT YEAR
  // ===============================
  const currentYearStudents = studentsData.filter((s) => {
    if (!s.admitted_on) return false;
    return new Date(s.admitted_on).getFullYear() === currentYear;
  });

  const getClassLabel = (id) => {
    const found = grade.find(g => String(g.value) === String(id));
    return found ? found.label : id;
  };

  // ===============================
  // FILTER + SEARCH
  // ===============================
  const filtered = currentYearStudents.filter((s) => {
    const matchGrade =
      !selectedGrade || String(s.class_admitted) === String(selectedGrade);

    const matchSearch =
      s.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.student_id?.toString().includes(searchTerm);

    return matchGrade && matchSearch;
  });

  // ===============================
  // RESET PAGE ON FILTER CHANGE
  // ===============================
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedGrade, searchTerm]);

  // ===============================
  // PAGINATION LOGIC
  // ===============================
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;

  const paginatedStudents = filtered.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // ===============================
  // PAGINATION UI
  // ===============================
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="d-flex justify-content-center align-items-center gap-2 flex-nowrap">

        <button
          className="btn btn-sm btn-outline-primary"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(prev => prev - 1)}
        >
          Previous
        </button>

        <button className="btn btn-sm btn-primary">
          {currentPage}
        </button>

        {currentPage + 1 <= totalPages && (
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => setCurrentPage(prev => prev + 1)}
          >
            {currentPage + 1}
          </button>
        )}

        {currentPage + 2 <= totalPages && (
          <span className="px-1">...</span>
        )}

        <button
          className="btn btn-sm btn-outline-primary"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(prev => prev + 1)}
        >
          Next
        </button>

      </div>
    );
  };

  // ===============================
  // LEDGER
  // ===============================
  const openLedger = (s) => {
    const normalized = {
      student_id: s.student_id ?? s.id,
      full_name: s.full_name ?? s.name ?? s.student_name,
      class_admitted: s.class_admitted ?? s.class_name ?? s.class
    };

    setSelectedStudent(normalized);
    setShowLedger(true);
  };

  const handleAdmissionSuccess = (student) => {
    setSelectedStudent(student);
    setShowLedger(true);
  };

  const getPhoto = (photo) => photo || "/default-avatar.png";

  // ===============================
  // RENDER
  // ===============================
  return (
    <Fragment>
      <div className="row">
        <div className="col-xl-12">

          {/* HEADER */}
          <div className="page-title flex-wrap">

            <div className="input-group search-area mb-md-0 mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search student..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="d-flex">
              <Form.Select
                size="sm"
                style={{ width: 200 }}
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
              >
                <option value="">All Classes</option>

                {[...new Map(
                  grade.map(g => [g.grade_id, g])
                ).values()].map(g => (
                  <option key={g.grade_id} value={g.grade_id}>
                    {g.grade_name}
                  </option>
                ))}
              </Form.Select>

              <span className="p-2" />

              <button
                className="btn btn-primary"
                onClick={() => admissionRef.current.openModal()}
              >
                + New Admission
              </button>

              <span className="p-2" />

              <button
                className="btn btn-success"
                onClick={refreshData}
                disabled={loading}
              >
                Refresh
              </button>
            </div>
          </div>

          {/* TABLE */}
          <div className="table-responsive full-data pb-50">
            <table className="table table-bordered table-hover">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Photo</th>
                  <th>Name</th>
                  <th>ID</th>
                  <th>Admission Date</th>
                  <th>Guardian</th>
                  <th>City</th>
                  <th>Contact</th>
                  <th>Class</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4">
                      Loading students...
                    </td>
                  </tr>
                ) : paginatedStudents.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4">
                      No students found
                    </td>
                  </tr>
                ) : (
                  paginatedStudents.map((s, idx) => (
                    <tr key={s.student_id}>
                      <td>{startIndex + idx + 1}</td>

                      {/* ✅ PHOTO COLUMN */}
                      <td>
                        {Boolean(s.pass_photo) ? (
                          <img
                            src={s.pass_photo}
                            loading="lazy"  // 🔥 PERFORMANCE BOOST
                            alt="Student"
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              objectFit: "cover",
                              cursor: "pointer"
                            }}
                            onClick={() => openPhotoModal(s.pass_photo)}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/default-avatar.png"; // 🔥 fallback
                            }}
                          />
                        ) : (
                          <img
                            src="/default-avatar.png"
                            loading="lazy"
                            alt="Default"
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              objectFit: "cover"
                            }}
                          />
                        )}
                      </td>

                      {/* ✅ OTHER DATA */}
                      <td>{s.full_name}</td>

                      <td className="text-primary">
                        #{s.student_id}
                      </td>

                      <td>{s.admitted_on?.slice(0, 10)}</td>

                      <td>{s.guardian_name}</td>
                      <td>{s.city}</td>
                      <td>{s.contact_no}</td>
                      <td>{getClassLabel(s.class_admitted)}</td>

                      {/* ✅ ACTION */}
                      <td>
                        <Dropdown className="float-end">
                          <Dropdown.Toggle className="btn sharp" as="div">
                            ⋮
                          </Dropdown.Toggle>

                          <Dropdown.Menu align="end">
                            <Dropdown.Item onClick={() => openLedger(s)}>
                              Ledger / Payment
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => openView(s)}>
                              View
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => openEdit(s)}>
                              Edit
                            </Dropdown.Item>
                            <Dropdown.Item>Delete</Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>

                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          <div className="d-flex justify-content-center gap-1 mt-3">
            {renderPagination()}
          </div>

        </div>
      </div>

      {/* MODALS */}
      <EditStudentModal
        show={showEdit}
        onHide={() => setShowEdit(false)}
        student={selectedStudent}
        refresh={refreshData}
      />

      <AdmissionModal
        ref={admissionRef}
        onSuccess={handleAdmissionSuccess}
      />

      <StudentLedgerModal
        show={showLedger}
        onClose={() => setShowLedger(false)}
        student={selectedStudent}
      />

      {/* PHOTO MODAL */}
      <Modal show={showPhoto} onHide={() => setShowPhoto(false)} centered>
        <Modal.Body className="text-center">
          <img
            src={selectedPhoto}
            alt="Student"
            style={{ maxWidth: "100%", borderRadius: 10 }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/default-avatar.png";
            }}
          />
        </Modal.Body>
      </Modal>

      {/* VIEW MODAL */}
      <Modal show={showView} onHide={() => setShowView(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Student Details</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedStudent && (
            <Row>
              <Col md={8}>
                <table className="table table-sm">
                  <tbody>
                    <tr>
                      <td><strong>Name</strong></td>
                      <td>{selectedStudent.full_name}</td>
                    </tr>

                    <tr>
                      <td><strong>Class</strong></td>
                      <td>{getClassLabel(selectedStudent.class_admitted)}</td>
                    </tr>

                    <tr>
                      <td><strong>Guardian</strong></td>
                      <td>{selectedStudent.guardian_name}</td>
                    </tr>
                  </tbody>
                </table>
              </Col>

              <Col md={4} className="text-center">
                {selectedStudent.pass_photo ? (
                  <img
                    src={selectedStudent.pass_photo || "/default-avatar.png"}
                    alt="Student"
                    style={{ width: "100%", maxWidth: 200 }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/default-avatar.png";
                    }}
                  />
                ) : "No Photo"}
              </Col>
            </Row>
          )}
        </Modal.Body>
      </Modal>

    </Fragment>
  );
};

export default Enrollment;